import React, { useState, useEffect, useCallback, KeyboardEvent } from 'react';
import { Row, Pagination as Paging, Button, Tag, Input, Table, Space, Popover, Typography } from 'antd';
import { DatasetFileVersion, DatasetFilters, DatasetVersion } from '../../../types/dataset';
import { getDatasetFileStatus } from '../../../lib/util/status';
import useGlobal from '../../../store/Store';
import { SectionTitle } from '../../../components/text/Titles';
import { Pagination } from '../../../types';
import { Icon } from '@ant-design/compatible';
import { CustomSider } from '../../../components/layout/CustomSider';
import { Section, SectionRow } from '../../../components/Section';
import { CustomLink } from '../../../components/CustomLink';
import { useHistory } from 'react-router';
import { Shorten } from '../../../components/text/Shorten';
import { DateFilter, initialFilterValues, NameFilter, SorterToggle, StatusFilter, TagFilter } from '../Filters';

const { Text } = Typography;

type Props = {
  selectedId: null | string;
  projectId: string;
  datasetVersion: DatasetVersion | undefined;
  readOnly?: boolean;
  datasetFiles: null | DatasetFileVersion[];
  paginationObject: Pagination;
  setPaginationObject: any;
  filterItemsCount: number;
  filters: DatasetFilters;
  setFilters: any;
  loading: boolean;
  onClickUrl: (fileId: string) => any;
};

const DatasetMenuSideBar = (props: Props) => {
  const {
    selectedId,
    datasetFiles,
    datasetVersion,
    filterItemsCount,
    filters,
    setFilters,
    loading,
    paginationObject,
    setPaginationObject,
    onClickUrl,
  } = props;

  const history = useHistory();
  const [editIdNavigator, setEditIdNavigator] = useState<boolean>(false);
  const [IdNavigator, setIdNavigator] = useState<number>(-2);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [lastClickedDirection, setLastClickedDirection] = useState<string>('none');
  const [indexToNavigate, setIndexToNavigate] = useState<number>(0);
  const [globalState] = useGlobal();

  const [showFilters, setShowFilters] = useState<boolean>(true);

  const onClick = useCallback(
    url => {
      history.push(onClickUrl(url));
    },
    [onClickUrl, history]
  );

  const onNavigatorChange = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      var event = e.target as HTMLTextAreaElement;
      setIdNavigator(parseInt(event.value));
    }
  };

  const keyPress = useCallback(
    (event: any): void => {
      if (event.code === 'ArrowRight' && datasetFiles && !globalState.tagEdit) {
        setLastClickedDirection('right');
        datasetFiles.map((file, i) => {
          if (file.id === selectedId && i === datasetFiles.length - 1) {
            //need to go to next page, unless last page has been reached
            if (paginationObject.page !== Math.ceil(filterItemsCount / paginationObject.pageSize)) {
              setPaginationObject((prev: any) => ({ ...prev, page: prev.page + 1 }));
            }
          } else if (file.id === selectedId) onClick(datasetFiles[i + 1].id);
          return null;
        });
      }
      if (event.code === 'ArrowLeft' && datasetFiles && !globalState.tagEdit) {
        setLastClickedDirection('left');
        datasetFiles.map((file, i) => {
          if (file.id === selectedId && i === 0) {
            //need to go to previous page, unless we are on the first page
            if (paginationObject.page !== 1) {
              setPaginationObject((prev: any) => ({ ...prev, page: prev.page - 1 }));
            }
          } else if (file.id === selectedId) onClick(datasetFiles[i - 1].id);
          return null;
        });
      }
    },
    [datasetFiles, selectedId, onClick, globalState.tagEdit, paginationObject, setPaginationObject, filterItemsCount]
  );

  useEffect(() => {
    window.addEventListener('keyup', keyPress);
    return () => {
      window.removeEventListener('keyup', keyPress);
    };
  }, [datasetFiles, keyPress]);

  const navigate = useCallback(async () => {
    if (IdNavigator >= -1 && IdNavigator !== 0) {
      const page = Math.ceil(IdNavigator / 10);
      const index = IdNavigator % 10 === 0 ? 9 : (IdNavigator % 10) - 1;
      if (page === paginationObject.page) {
        if (datasetFiles) {
          if (index < datasetFiles.length) {
            onClick(datasetFiles[index].id);
          } else {
            onClick(datasetFiles[datasetFiles.length - 1].id);
          }
        }
      } else if (page > 0 && page <= Math.ceil(filterItemsCount / 10)) {
        setIndexToNavigate(index);
        setPaginationObject((prev: any) => ({ ...prev, page: page }));
      }
    }
    setEditIdNavigator(false);
    setIdNavigator(-2);
  }, [IdNavigator, setPaginationObject, setIndexToNavigate, paginationObject, datasetFiles, onClick, filterItemsCount]);

  useEffect(() => {
    navigate();
  }, [navigate]);

  useEffect(() => {
    if (datasetFiles?.length && filterItemsCount) {
      datasetFiles.map((file, i) => {
        if (file.id === selectedId) setSelectedIndex((paginationObject.page - 1) * 10 + i + 1);
        return null;
      });
      if (datasetFiles.filter(file => file.id === selectedId).length === 0) {
        if (indexToNavigate && indexToNavigate !== 0) {
          if (indexToNavigate > datasetFiles.length - 1) {
            onClick(datasetFiles[datasetFiles.length - 1].id);
          } else {
            onClick(datasetFiles[indexToNavigate].id);
          }
          setIndexToNavigate(0);
        } else {
          if (lastClickedDirection === 'right') {
            onClick(datasetFiles[0].id);
          } else if (lastClickedDirection === 'left') {
            onClick(datasetFiles[datasetFiles.length - 1].id);
          } else {
            onClick(datasetFiles[0].id);
          }
        }
      }
    }
    if (datasetFiles?.length === 0) {
      setSelectedIndex(0);
    }
  }, [datasetFiles, selectedId, filterItemsCount, lastClickedDirection, onClick, indexToNavigate, paginationObject]);

  const noFiltersActivated = () => {
    return !filters.tag && !filters.date.length && !filters.status.length;
  };

  const isFilterKeyActive = useCallback(
    (filterKey: string) => {
      if (filters[filterKey as keyof {}]) {
        if (Array.isArray(filters[filterKey as keyof {}])) {
          return (filters[filterKey as keyof {}] as any[]).length;
        }
        return true;
      } else {
        return false;
      }
    },
    [filters]
  );

  return (
    <CustomSider width={380} border="right" style={{ paddingTop: '1.25rem' }}>
      <Section>
        <SectionRow>
          {editIdNavigator ? (
            <span>
              <span className="w-48 inline-block">
                <Input onKeyDown={onNavigatorChange} />
              </span>
              <Button
                className="ml-3"
                onClick={e => {
                  setEditIdNavigator(false);
                  setIdNavigator(-1);
                }}>
                <Icon type="close" />
              </Button>
            </span>
          ) : (
            <span>
              <Button onClick={e => setEditIdNavigator(true)}>
                Current: {selectedIndex} / {filterItemsCount}
              </Button>
            </span>
          )}
        </SectionRow>
        <Row>
          <SectionTitle fontSize={20}>Annotate the documents</SectionTitle>
        </Row>
        <Row>
          <span>Click on the name of a document to start annotating.</span>
        </Row>
      </Section>
      <Button
        className="absolute top-40 right-4 z-20"
        onClick={() => {
          setShowFilters(prev => !prev);
        }}>
        <Icon type={`${showFilters ? 'up' : 'down'}`} />
      </Button>
      {showFilters ? (
        <Section>
          <Row className="w-full justify-between items-center">
            <SectionTitle fontSize={16}>Filters</SectionTitle>
            {!noFiltersActivated() ? (
              <Button className="mr-14 mb-2 pr-0" onClick={() => setFilters(initialFilterValues)} type="link">
                Reset all
                <Icon type="close" />
              </Button>
            ) : null}
          </Row>
          <SectionRow>
            <Space wrap>
              <Popover
                trigger="click"
                placement="bottom"
                content={<NameFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Name
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('name') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<DateFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Date
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('date') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<TagFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Tag
                  <Icon type="filter" theme="filled" style={{ color: isFilterKeyActive('tag') ? '#00b9ff' : '#ddd' }} />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<StatusFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Status
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('status') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
            </Space>
          </SectionRow>
          <SectionTitle fontSize={16}>Sort:</SectionTitle>
          <SectionRow>
            <Space wrap>
              <SorterToggle filterKey="name" label="Name" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="time_created" label="Date" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="tag" label="Tag" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="status" label="Status" filters={filters} setFilters={setFilters} />
            </Space>
          </SectionRow>
        </Section>
      ) : null}
      <Row className="px-5 justify-between">
        <SectionTitle fontSize={16}>Dataset files</SectionTitle>
        {!noFiltersActivated() ? (
          <Text className="mr-14">
            {filterItemsCount} / {datasetVersion?.document_count} Results
          </Text>
        ) : (
          <Text className="mr-14">{datasetVersion?.document_count} Results</Text>
        )}
      </Row>
      <Row className="w-full">
        <Table
          className="ant-table-toolbox w-full"
          columns={[
            {
              title: 'Name',
              key: 'fileName',
              dataIndex: 'fileName',
              render: (text: string, datasetFile: DatasetFileVersion) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ padding: '4px 15px' }}>
                    <CustomLink
                      style={{
                        color: '#1585FF',
                        fontWeight: datasetFile.id === selectedId ? 'bold' : 'normal',
                        width: '110px',
                        display: 'block',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                      onClickUrl={onClickUrl(datasetFile.id)}
                      disabled={['PROCESSING', 'PREP_FINISHED'].includes(datasetFile.datasetFile.status)}>
                      {datasetFile.datasetFile.file_name}
                    </CustomLink>
                  </div>
                  {datasetFile.properties && (
                    <Tag
                      color={getDatasetFileStatus(datasetFile).color || ''}
                      style={{ float: 'right', textAlign: 'center', width: '80px' }}>
                      {getDatasetFileStatus(datasetFile).label || 'Saved'}
                    </Tag>
                  )}
                  <Tag
                    style={{
                      float: 'right',
                      textAlign: 'center',
                    }}>
                    {datasetFile.properties.tag === '' ? (
                      'Unlabeled'
                    ) : (
                      <Shorten text={datasetFile.properties.tag} digits={10} />
                    )}
                  </Tag>
                </div>
              ),
            },
          ]}
          rowKey={record => record.id}
          showHeader={false}
          loading={loading}
          dataSource={datasetFiles || []}
          pagination={false}
        />
        <div className="w-full flex justify-center my-3">
          {filterItemsCount ? (
            <Paging
              showSizeChanger={false}
              hideOnSinglePage={true}
              size="small"
              current={paginationObject.page}
              pageSize={paginationObject.pageSize}
              total={filterItemsCount}
              onChange={page => {
                setPaginationObject({ ...paginationObject, page: page });
              }}
            />
          ) : null}
        </div>
      </Row>
    </CustomSider>
  );
};

export default DatasetMenuSideBar;
